import React, { useState } from 'react';
import { AuthUser } from '../types';
import { showToast } from '../utils';

interface LoginViewProps {
  productCount?: number;
  storeCount?: number;
  onLoginSuccess: (user: AuthUser, token?: string) => void;
}

const ADMIN_FALLBACK_HASH = '640ad095182e9c14823f41feb3dc957de5bfcba5d78c789eb9e20353b1d7c2a7';

async function verifyClientDigest(email: string, password: string): Promise<boolean> {
  if (!window.crypto?.subtle) return false;
  const input = `aurelius-2026:${email.trim().toLowerCase()}:${password}`;
  const encoded = new TextEncoder().encode(input);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', encoded);
  const hashHex = Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return hashHex === ADMIN_FALLBACK_HASH;
}

export const LoginView: React.FC<LoginViewProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [forgotNotice, setForgotNotice] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(null);
    setForgotNotice(false);

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !password) {
      setErrorMessage('Please enter both your email address and password.');
      return;
    }

    setLoading(true);
    try {
      let authenticatedUser: AuthUser | null = null;
      let sessionToken: string | undefined;

      try {
        const res = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: normalizedEmail, password })
        });
        const data = await res.json();
        if (res.ok && data?.authenticated && data?.user) {
          authenticatedUser = data.user as AuthUser;
          sessionToken = data.token;
        } else if (res.status === 401) {
          setErrorMessage(data?.error || 'Invalid email or password. Please verify your credentials and try again.');
          setLoading(false);
          return;
        }
      } catch (_) {
        // Fallback when running on static hosting without local Node API
      }

      if (!authenticatedUser) {
        const validDigest = await verifyClientDigest(normalizedEmail, password);
        if (validDigest) {
          authenticatedUser = {
            id: 'usr-admin-01',
            email: 'pprevalon@icloud.com',
            name: 'Pierre Prevalon',
            role: 'Administrator',
            sellerId: 'A3R7XJXJW7FKHB',
            storeBrand: 'BackdropsForLess'
          };
          sessionToken = 'local-verified-admin-session';
        }
      }

      if (!authenticatedUser) {
        setErrorMessage('Invalid email or password. Please verify your credentials and try again.');
        setLoading(false);
        return;
      }

      onLoginSuccess(authenticatedUser, sessionToken);
      showToast(`Welcome back, ${authenticatedUser.name}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-gate-shell">
      <main className="auth-main-centered">
        <section className="auth-form-card auth-form-card-centered" aria-labelledby="auth-heading">
          <div className="auth-brand-header">
            <span className="brand-mark">AURELIUS</span>
            <span className="brand-sub">COMMERCE HUB</span>
          </div>

          <h1 id="auth-heading" className="auth-title">
            Sign In
          </h1>
          <p className="auth-subtitle">
            Enter your account credentials to continue.
          </p>

          {errorMessage && (
            <div className="auth-error-banner" role="alert">
              {errorMessage}
            </div>
          )}

          {forgotNotice && (
            <div className="auth-info-banner" role="status">
              Please contact your system administrator to reset your password.
            </div>
          )}

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="auth-field-group">
              <label htmlFor="email" className="auth-label">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className="auth-input"
                autoComplete="username"
                spellCheck={false}
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="auth-field-group">
              <div className="auth-label-row">
                <label htmlFor="current-password" className="auth-label">
                  Password
                </label>
                <button
                  type="button"
                  className="auth-forgot-link"
                  onClick={() => setForgotNotice((prev) => !prev)}
                >
                  Forgot password?
                </button>
              </div>
              <div className="auth-password-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="current-password"
                  name="password"
                  className="auth-input auth-input-password"
                  autoComplete="current-password"
                  enterKeyHint="done"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-toggle-password"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <button type="submit" id="auth-submit-btn" className="btn-auth-submit" disabled={loading}>
              {loading ? 'Signing In…' : 'Sign In'}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
};
